import { type BrandZohoTypes } from "@elit-hair/brands";
import { parsePhoneNumberWithError } from "libphonenumber-js";

import type { Call } from "@/types/call.types";
import { getPrimaryPhoneNumber } from "@/utils/crm-module.utils";
import { getZohoCrmUrl } from "@/utils/get-zoho-crm-url";

const OPEN_IN_NEW_CONTEXT_OPTIONS = "noopener,noreferrer" as const;

type RecordInfo = BrandZohoTypes["Leads"] | BrandZohoTypes["Contacts"] | undefined;

export type ChannelAction = {
    isAvailable(): boolean;
    getHref(): string | undefined;
    execute(): void;
};

export function createEmailAction(
    recordInfo: RecordInfo,
    callData: Call | undefined,
    isActive: boolean
): ChannelAction {
    const email =
        typeof recordInfo?.Email === "string" ? recordInfo.Email.trim() : (callData?.calledEmail?.trim() ?? undefined);
    const href = email ? `mailto:${email}` : undefined;

    return {
        isAvailable: () => Boolean(isActive && href),
        getHref: () => href,
        execute: () => {
            if (!isActive || !href) return;
            window.open(href, "_blank", OPEN_IN_NEW_CONTEXT_OPTIONS);
        },
    };
}

export function createWhatsappAction(
    recordInfo: RecordInfo,
    callData: Call | undefined,
    isActive: boolean
): ChannelAction {
    let href: string | undefined;

    try {
        const phone =
            recordInfo && callData?.moduleType
                ? getPrimaryPhoneNumber(recordInfo, callData.moduleType)
                : (callData?.calledNumber ?? null);

        if (phone) {
            const parsed = parsePhoneNumberWithError(phone);
            href = `whatsapp://send?phone=${parsed.format("E.164").replace("+", "")}`;
        }
    } catch {
        const cleaned = callData?.calledNumber?.replace(/\D/g, "");
        href = cleaned ? `whatsapp://send?phone=${cleaned}` : undefined;
    }

    return {
        isAvailable: () => Boolean(isActive && href),
        getHref: () => href,
        execute: () => {
            if (!isActive || !href) return;
            window.open(href, "_blank", OPEN_IN_NEW_CONTEXT_OPTIONS);
        },
    };
}

type BrandKey = Parameters<typeof getZohoCrmUrl>[0];

export function createCrmAction(
    recordInfo: RecordInfo,
    callData: Call | undefined,
    selectedBrand: BrandKey,
    isActive: boolean
): ChannelAction {
    const recordId = recordInfo?.id ?? callData?.recordId;
    const moduleType = callData?.moduleType;
    const href = recordId && moduleType ? (getZohoCrmUrl(selectedBrand, moduleType, recordId) ?? undefined) : undefined;

    return {
        isAvailable: () => Boolean(isActive && href),
        getHref: () => href ?? undefined,
        execute: () => {
            if (!isActive || !href) return;
            window.open(href, "_blank", OPEN_IN_NEW_CONTEXT_OPTIONS);
        },
    };
}
